import { useMemo } from 'react';
import { AccountStatus } from 'shared/legacy/contexts/CandidateContext/CandidateEmailSettingsContext.types';
import { useIndeedConfig } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedConfig';
import { useLinkedinConfig } from 'shared/legacy/hooks/useBrowserExtension/linkedin/useLinkedinConfig';
import { useLinkedinInMailConfig } from 'shared/legacy/hooks/useBrowserExtension/linkedinInMail/useLinkedinInMailConfig';
import { useOauthNylasStatusQuery } from 'shared/legacy/hooks/useOauthNylasStatusQuery';

export type CommunicationChannel =
  | 'linkedin'
  | 'linkedinInmail'
  | 'indeed'
  | 'email'
  | 'whatsapp'
  | 'rjCommunicationHub';

export interface CommunicationChannelsStatus {
  linkedin: {
    isConnected: boolean;
    status: string;
  };
  linkedinInmail: {
    isConnected: boolean;
    status: string;
  };
  indeed: {
    isConnected: boolean;
    status: string;
  };
  email: {
    isConnected: boolean;
    status: AccountStatus;
  };
  whatsapp: {
    isConnected: boolean;
    status: string;
  };
  rjCommunicationHub: {
    isConnected: boolean;
    status: string;
  };
  hasDisconnectedChannels: boolean;
}

export const useCommunicationChannelsStatus = (): CommunicationChannelsStatus => {
  const linkedinConfig = useLinkedinConfig();
  const linkedinInmailConfig = useLinkedinInMailConfig();
  const indeedConfig = useIndeedConfig();
  const { data: { email: emailStatus } = {} } = useOauthNylasStatusQuery();

  const channelsStatus = useMemo<CommunicationChannelsStatus>(() => {
    const linkedin = {
      isConnected: linkedinConfig.isLoggedIn,
      status: linkedinConfig.status,
    };

    const linkedinInmail = {
      isConnected: linkedinInmailConfig.isLoggedIn,
      status: linkedinInmailConfig.status,
    };

    const indeed = {
      isConnected: indeedConfig.isLoggedIn,
      status: indeedConfig.status,
    };

    const email = {
      isConnected: emailStatus === AccountStatus.Valid,
      status: emailStatus || AccountStatus.NonExistent,
    };

    // WhatsApp is always considered connected as it doesn't require platform login
    const whatsapp = {
      isConnected: true,
      status: 'Connected',
    };

    // RJ Communication Hub is always considered connected as it's an internal AI system
    const rjCommunicationHub = {
      isConnected: true,
      status: 'Connected',
    };

    const hasDisconnectedChannels =
      !linkedin.isConnected || !linkedinInmail.isConnected || !indeed.isConnected || !email.isConnected;

    return {
      linkedin,
      linkedinInmail,
      indeed,
      email,
      whatsapp,
      rjCommunicationHub,
      hasDisconnectedChannels,
    };
  }, [linkedinConfig, indeedConfig, emailStatus, linkedinInmailConfig]);

  return channelsStatus;
};
